import type { MaybeProductListItems } from 'domain/product-list'
import type { FC, RefObject } from 'react'

import { LinkButton } from '@s-group/design-system-components'
import { StyledSyncLoader } from 'components/common/StyledSyncLoader'
import { InfiniteScroll } from 'components/InfiniteScroll'
import { ProductListItem } from 'components/ProductListItem'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'

import { ProductCardsGrid } from './Product/ProductCardsGrid'

const PageButton = styled(LinkButton)({
  [StyledSyncLoader]: {
    display: 'block',
    transform: 'scale(0.75)',
  },
})

interface ProductListProps {
  className?: string
  hasMore?: boolean
  loading?: boolean
  loadingMore?: boolean
  loadMore: () => Promise<void>
  page?: number
  products?: MaybeProductListItems
  root?: RefObject<HTMLDivElement>
}

const _ProductList: FC<ProductListProps> = ({
  className,
  hasMore,
  loading,
  loadingMore,
  loadMore,
  page,
  products,
  root,
}) => {
  const isClientSide = useIsClientSide()
  const router = useRouter()
  const { t } = useTranslation()

  const usesPagination = !!page

  /**
   * The purpose of this href is to be a "previous page link" that
   * preserves all other URL params but decrements the `page`
   * by one. The first visible one should be `page=2`.
   */
  const previousHref = useMemo(() => {
    if (!page) return router.asPath
    const prevPage = page - 1 > 1 ? page - 1 : null
    const query = new URLSearchParams(Object.entries({ ...router.query, page: `${prevPage}` }))

    if (prevPage !== null) {
      query.set('page', `${prevPage}`)
    } else {
      query.delete('page')
    }

    if (Object.keys(query).length === 0) {
      return router.pathname
    }

    return `${router.pathname}?${query}`
  }, [page, router.asPath, router.pathname, router.query])

  /** The "next page link" that increments `page` while leaving other params as-is */
  const nextHref = useMemo(() => {
    const nextPage = `${(page || 1) + 1}`
    const query = new URLSearchParams(Object.entries({ ...router.query, page: nextPage }))
    return `${router.pathname}?${query}`
  }, [page, router.pathname, router.query])

  /**
   * To enable personalized results without flashing SSR results, we hide the
   * (SSR) result list for the first render. The effect hook below will then
   * set them to be visible on the second client-side render.
   *
   * Further down in the JSX, we apply a <styled> tag inside a <noscript> tag
   * to force the (SSR) result list to be visible.
   *
   * This results in the category page displaying products and pagination buttons
   * when JavaScript is disabled, while also nicely avoiding a flash of "double-search"
   * client-side when JavaScript is enabled (because otherwise the SSR results would be
   * visibly replaced with personalized search results).
   */
  const [clientSideResultsHidden, setHidden] = useState(!isClientSide)
  useEffect(() => {
    setHidden(false)
  }, [])

  return (
    <div className={className} data-test-id="product-list">
      {page && page > 1 && (
        <PageButton href={previousHref} rel="prev" rounding="small">{t`Previous Page`}</PageButton>
      )}

      {(loading || clientSideResultsHidden) && <ProductCardsGrid.Loader />}

      <ProductCardsGrid.Grid
        role="list"
        style={{ display: clientSideResultsHidden ? 'none' : undefined }}
      >
        <InfiniteScroll
          /** Disabled when using pagination */
          disabled={loading || usesPagination}
          onScrolledToBottom={loadMore}
          root={root?.current}
          rootMargin="400px"
        >
          {products?.map(
            (product, i) =>
              product && (
                <TrackingContextProvider
                  listPosition={i}
                  key={i /** search might contain duplicate EANs when using pagination */}
                >
                  <ProductListItem product={product} data-test-id="product-list-item" />
                </TrackingContextProvider>
              ),
          )}
        </InfiniteScroll>
      </ProductCardsGrid.Grid>
      <noscript>
        <style
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `${ProductCardsGrid.Grid} { display: grid !important } ${CenteredLoader} { display: none }`,
          }}
        />
      </noscript>

      {hasMore && usesPagination && (
        <PageButton rel="next" href={nextHref} primary rounding="small">{t`Next Page`}</PageButton>
      )}

      {
        /**
         * Show a `<noscript>` next button on the first page,
         * for Googlebot to realise it's there. On the following
         * pages the regular button from above will be shown,
         * because then the url contains `?page=X`.
         */
        hasMore && !usesPagination && (
          <noscript>
            <PageButton
              href={nextHref}
              rel="next"
              primary
              rounding="small"
            >{t`Next Page`}</PageButton>
          </noscript>
        )
      }

      {
        /** Instead of pagination buttons, display infinite scroller for regular visitors */
        hasMore && loadingMore && !usesPagination && isClientSide && (
          <CenteredLoader data-test-id="loading-more-products" />
        )
      }
    </div>
  )
}

_ProductList.displayName = 'ProductList'

const CenteredLoader = styled(StyledSyncLoader)(({ theme }) => ({
  margin: `${theme.spacings.large} auto`,
}))

export const ProductList = styled(_ProductList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  [`> ${PageButton}`]: {
    margin: `${theme.spacings.large} auto`,
  },

  noscript: {
    display: 'flex',

    [`> ${PageButton}`]: {
      margin: `${theme.spacings.large} auto`,
    },
  },
}))
